import { useEffect, useState, useMemo, useCallback } from 'react';
import { Brand, Country, getNotificationConfigByBrandAndCountry } from 'api';
import { TSubmitTrackAndTraceNotificationConfigPayload } from 'api/type';

type TUseTrackAndTraceCommonProps = {
  brand?: Brand;
  country?: Country;
}


type TTrackAndTraceNotificationConfig = Omit<TSubmitTrackAndTraceNotificationConfigPayload, 'brand' | 'countryIsoCode'> & { enabled: boolean}
type TTrackAndTraceNotificationConfigKey = keyof TTrackAndTraceNotificationConfig;

export const useNotificationConfigs = (
  {
    brand,
    country
  }: TUseTrackAndTraceCommonProps
) => {
  const [isNotificationLoading, setIsNotificationLoading] = useState(false);
  const [trackAndTraceNotificationOpen, setTrackAndTraceNotificationOpen] = useState(false);
  const [trackAndTraceNotificationConfig, setTrackAndTraceNotificationConfig] = useState<TTrackAndTraceNotificationConfig>({
    enabled: false,
    preExpiredReminderEnabled: false,
    postExpiredReminderEnabled: false,
    preExpiredReminderInMinutes: 0,
    postExpiredReminderIntervalInMinutes: 0,
  });

  const getNotificationConfig = useCallback(() => {
    if (!brand?.id || !country?.id) return;
    setIsNotificationLoading(true);
    const fetchNotificationConfig = async (brandId: string, countryIsoCode: string) => {
      try {
      const notificationConfig = await getNotificationConfigByBrandAndCountry({
        brand: brand.id,
        countryIsoCode: country.id
        });
        const enabled = notificationConfig.postExpiredReminderEnabled || notificationConfig.preExpiredReminderEnabled
        setTrackAndTraceNotificationConfig({enabled, ...notificationConfig});
      } catch (error) {
        console.error('Failed to fetch notification config:', error);
      } finally {
        setIsNotificationLoading(false);
      }
    }
    fetchNotificationConfig(brand.id, country.id);
  }, [brand, country]);

  const closeTrackAndTraceNotificationModal = useCallback(() => {
    setTrackAndTraceNotificationOpen(false);
    setTrackAndTraceNotificationConfig({
      enabled: false,
      preExpiredReminderEnabled: false,
      postExpiredReminderEnabled: false,
      preExpiredReminderInMinutes: 0,
      postExpiredReminderIntervalInMinutes: 0,
    });
  }, []);

  useEffect(() => {
    if (trackAndTraceNotificationOpen) {
      getNotificationConfig();
    }
  }, [trackAndTraceNotificationOpen, getNotificationConfig]);

  const updateTrackAndTraceNotificationConfig = useCallback((key: TTrackAndTraceNotificationConfigKey, value: boolean | number) => {
    setTrackAndTraceNotificationConfig(prev => ({ ...prev, [key]: value }));
  }, []);

    // Validation for Track & Trace notification config
    const isTrackAndTraceNotificationConfigValid = useMemo(() => {
      const { enabled, preExpiredReminderEnabled, postExpiredReminderEnabled, preExpiredReminderInMinutes, postExpiredReminderIntervalInMinutes } = trackAndTraceNotificationConfig;
      
      if (!enabled) return true;

      // If preExpiredReminderEnabled is true, preExpiredReminderInMinutes must be > 0
      if (preExpiredReminderEnabled && preExpiredReminderInMinutes <= 0) {
        return false;
      }
      
      // If postExpiredReminderEnabled is true, postExpiredReminderIntervalInMinutes must be > 0
      if (postExpiredReminderEnabled && postExpiredReminderIntervalInMinutes <= 0) {
        return false;
      }
      
      return true;
    }, [trackAndTraceNotificationConfig]);

  return useMemo(() => ({
    trackAndTraceNotificationConfig,
    getNotificationConfig,
    trackAndTraceNotificationOpen,
    closeTrackAndTraceNotificationModal,
    isTrackAndTraceNotificationConfigValid,
    updateTrackAndTraceNotificationConfig,
    setTrackAndTraceNotificationOpen,
    isNotificationLoading
  }), [
    trackAndTraceNotificationConfig,
    getNotificationConfig,
    trackAndTraceNotificationOpen,
    closeTrackAndTraceNotificationModal,
    isTrackAndTraceNotificationConfigValid,
    updateTrackAndTraceNotificationConfig,
    setTrackAndTraceNotificationOpen,
    isNotificationLoading
  ]);
}